import Slider from 'rc-slider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '../../../styled-components/components/Alert';

interface InputSliderProps {
  title: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  current: number;
  unit: string;
  isPending: boolean;
  rangeLabel?: {
    min: string | number;
    max: string | number;
    unit: string;
  };
  onChange: (value: number) => void;
}

const InputSlider = ({
  title,
  min,
  max,
  step,
  defaultValue,
  current,
  unit,
  isPending,
  rangeLabel,
  onChange,
}: InputSliderProps) => {
  const { t } = useTranslation(['validation']);
  const [localValue, setLocalValue] = useState(() => current);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;

  useEffect(() => {
    setLocalValue(current);
  }, [current]);

  const hasNoRange = min === 0 && max === 0;

  if (hasNoRange) {
    return <div />;
  }

  const isNotEditable = min === max;

  if (isNotEditable) {
    return (
      <div className="m-t">
        <div data-wx-inputlabel="">{title}</div>
        <div className="h5 no-margin">
          {current}
          {unit}
        </div>
      </div>
    );
  }

  const onChangeSlider = (_value: number | number[]) => {
    const value = Array.isArray(_value) ? _value[0] : _value;
    setLocalValue(value);
  };

  const onChangeSliderComplete = () => {
    onChange(localValueRef.current);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseInt(e.currentTarget.value, 10);
    setLocalValue(value);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const isValid = localValueRef.current >= min && localValueRef.current <= max;
      if (isValid) {
        onChange(localValueRef.current);
      }
    }, 500);
  };

  const onBlur = () => {
    clearTimeout(timeoutRef.current);
    const isValid = localValueRef.current >= min && localValueRef.current <= max;
    if (isValid) {
      onChange(localValueRef.current);
    }
  };

  const isValidInput = !Number.isNaN(localValue) && localValue >= min && localValue <= max;

  return (
    <div className="m-t">
      <div data-wx-inputlabel="">{title}</div>
      <div data-am-inputrow="always valign-middle">
        <div className="input-row">
          <div className="input-row-item">
            <div data-wx-inputtext="unit">
              <input
                type="text"
                placeholder={title}
                value={Number.isNaN(localValue) ? '' : localValue.toFixed(0)}
                onChange={onInputChange}
                onBlur={onBlur}
              />
              <div className="input-text-unit">{unit}</div>
              <div className="input-text-ghost" />
            </div>
          </div>

          <div className="input-row-item">
            <div data-wx-rangeslider="simple">
              <div className="range-slider">
                {isValidInput && (
                  <Slider
                    min={min}
                    max={max}
                    step={step}
                    defaultValue={defaultValue}
                    value={localValue}
                    disabled={isPending}
                    onChange={onChangeSlider}
                    onChangeComplete={onChangeSliderComplete}
                  />
                )}
              </div>
              {rangeLabel !== undefined && (
                <div className="range-span">
                  <div className="range-span-low">
                    {rangeLabel.min}
                    {rangeLabel.unit}
                  </div>
                  <div className="range-span-high">
                    {rangeLabel.max}
                    {rangeLabel.unit}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isValidInput && (
          <div className="m-t">
            <Alert tone="error">
              {t('errorValueBetweenTwoNumbers', { ns: 'validation', low: min, high: max })}
            </Alert>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputSlider;
